import { Apollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { ApolloClient, ApolloLink, CombinedGraphQLErrors, ErrorLike, InMemoryCache } from '@apollo/client';
import { NgZone } from '@angular/core';
import { SetContextLink } from '@apollo/client/link/context';
import { NgModule } from '@angular/core';

import { environment } from 'src/environments/environment';
import { UserService } from '../services/user.service';
import { ErrorCode } from '../models/error-code.enum';
import { Router } from '@angular/router';
import { MessageService } from '../services/message.service';
import { HttpService } from '../services/http.service';

// AC4 は defaultOptions の errorPolicy を型にも宣言しておく必要がある（結果の型が errorPolicy で変わるため）
declare module '@apollo/client' {
  export namespace ApolloClient {
    export namespace DeclareDefaultOptions {
      interface WatchQuery {
        errorPolicy: 'all';
      }
      interface Query {
        errorPolicy: 'all';
      }
      interface Mutate {
        errorPolicy: 'all';
      }
    }
  }
}

@NgModule({
  providers: [Apollo, HttpLink],
})
export class GraphQLModule {
  constructor(
    private router: Router,
    private apollo: Apollo,
    private httpLink: HttpLink,
    private httpService: HttpService,
    private userService: UserService,
    private messageService: MessageService,
    private zone: NgZone,
  ) {
    const uri = environment.api.gql_url;
    const http = this.httpLink.create({ uri });

    const basic = new SetContextLink(() => ({
      headers: {
        Accept: 'charset=utf-8',
      },
    }));

    const auth = new SetContextLink(async (_prevContext, operation) => {
      const errCode = await this.userService.refresh(operation.operationName);
      if (errCode) {
        switch (errCode) {
          case ErrorCode.AuthenticateError:
            this.userService.forceLogout(ErrorCode.AuthenticateError, (err) => {
              setTimeout(() => {
                this.messageService.setFlashMessage(
                  'warn',
                  '警告',
                  '認証情報の有効期限が切れました。ログアウトします。お手数ですが、再ログインをお願いいたします。',
                );
                this.router.navigate(['/login']);
              }, 200);
            });
            return {};
          default:
            this.httpService.handleError(errCode);
            return {};
        }
      }
      const token = userService.getUserToken();
      if (token) {
        return {
          headers: {
            'x-app-authorization': token,
          },
        };
      }
      return {};
    });

    this.apollo.client = this.zone.runOutsideAngular(() => createApolloClient(ApolloLink.from([basic, auth, http])));
  }
}

export function createApolloClient(link: ApolloLink): ApolloClient {
  const client = new ApolloClient({
    link,
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        errorPolicy: 'all',
        fetchPolicy: 'network-only',
      },
      query: {
        errorPolicy: 'all',
        // no-cache: 本アプリはキャッシュ読み取り(watchQuery/readQuery等)を一切使わないため、
        // InMemoryCacheへの正規化書き込みは純粋なオーバーヘッド。
        // 特にyamada等の大容量レスポンス(在庫10万件超)では正規化がモバイル端末ハングの原因になる。
        fetchPolicy: 'no-cache',
      },
      mutate: {
        errorPolicy: 'all',
      },
    },
  });

  // AC4 の errorPolicy 'all' は通信エラーも reject せず `{ data: undefined, error }` で返す（AC3 は reject だった）。
  // 呼び出し側は data だけを見る箇所が多く、そのままだと通信エラーが 0 件表示や原因の消えた TypeError に化ける。
  // *GQL の fetch / mutate が通る client.query / client.mutate で GraphQL エラー以外を reject に戻す。GraphQL エラーは従来どおり結果の error に残る。
  // watch()（watchQuery）は包んでいない。使うようになったら同じ扱いが要る
  const query = client.query.bind(client);
  const mutate = client.mutate.bind(client);
  client.query = (async (options) => rejectUnlessGraphQLError(await query(options))) as typeof client.query;
  client.mutate = (async (options) => rejectUnlessGraphQLError(await mutate(options))) as typeof client.mutate;
  return client;
}

function rejectUnlessGraphQLError<T extends { error?: ErrorLike }>(result: T): T {
  if (result.error && !CombinedGraphQLErrors.is(result.error)) {
    throw result.error;
  }
  return result;
}
